/**
 * Persistent draft storage contracts.
 *
 * Two surfaces:
 *  - `DraftStorage` — durable, cross-device. Backed by Telegram CloudStorage.
 *  - `BackupStorage` — single in-flight draft, per-device. Backed by
 *    `localStorage`. Survives page reload, lets us prompt for recovery.
 *
 * `state` is the JSON form of a Lexical EditorState — the same shape
 * `editor.getEditorState().toJSON()` returns. We treat it as opaque JSON
 * so this module never imports lexical.
 */

export interface DraftMeta {
  /** Stable UUID. Generated client-side, never reused. */
  id: string;
  /** First ~60 chars of plain text or "Untitled". */
  title: string;
  /** First ~120 chars of plain text — for the drafts list preview. */
  preview: string;
  /** ISO-8601 timestamp. */
  updated_at: string;
  /** ISO-8601 timestamp. */
  created_at: string;
  /** Number of CloudStorage chunks the serialized state spans. */
  chunk_count: number;
  /**
   * True once the user has manually renamed this draft. Autosave must not
   * override the title in that case (mirrors desktop's
   * `title = CASE WHEN title = 'Untitled' THEN derived ELSE title END`).
   */
  title_locked?: boolean;
}

export type SerializedEditorStateJson = Record<string, unknown>;

/**
 * Media attachment metadata persisted alongside a draft so the media
 * survives draft switches / reloads. We persist Telegram's `file_id`
 * (the canonical reference for re-using a previously-uploaded file in a
 * prepared inline message) together with enough metadata to render the
 * slot UI without a fresh upload. For photos we also persist a small
 * base64 thumbnail so the preview survives reopen — for video/audio the
 * slot falls back to a kind-icon until the user re-attaches.
 */
export interface PersistedMedia {
  kind: "photo" | "video" | "audio";
  /** Telegram file_id from the original upload — reusable in shareMessage. */
  fileId: string;
  name: string;
  mime: string;
  size: number;
  /** Tiny JPEG dataURL for photo previews after restore. Photos only. */
  thumbDataUrl?: string;
}

export interface DraftRecord {
  meta: DraftMeta;
  state: SerializedEditorStateJson;
  media?: PersistedMedia;
}

export interface DraftStorage {
  list(): Promise<DraftMeta[]>;
  load(id: string): Promise<DraftRecord | null>;
  save(
    id: string,
    state: SerializedEditorStateJson,
    title: string,
    media?: PersistedMedia | null,
  ): Promise<DraftMeta>;
  /**
   * Manual rename — updates the index entry only, never touches chunks.
   * Sets `title_locked` so subsequent autosave calls preserve the new
   * title even as the user keeps typing.
   */
  rename(id: string, title: string): Promise<DraftMeta>;
  delete(id: string): Promise<void>;
}

export interface BackupRecord {
  state: SerializedEditorStateJson;
  saved_at: string;
}

export interface BackupStorage {
  saveCurrent(state: SerializedEditorStateJson): void;
  loadCurrent(): BackupRecord | null;
  clearCurrent(): void;
}

export class CloudStorageUnavailableError extends Error {
  constructor() {
    super("Telegram CloudStorage is not available in this client");
    this.name = "CloudStorageUnavailableError";
  }
}

export class DraftQuotaExceededError extends Error {
  constructor() {
    super(
      "Telegram CloudStorage quota exceeded — delete old drafts to continue",
    );
    this.name = "DraftQuotaExceededError";
  }
}
